import { AnnouncementListItemProps } from "./AnnouncementListItem.types";
import { Button } from "antd";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import ConfirmationModal from "../../../../../../Containers/ConfirmationModal";
import { useCallback, useState } from "react";
import { deleteAnnouncement } from "../../../../../../Requests/announcement-requests";
import {
	NOTIFICATION_TYPES,
	openNotification,
} from "../../../../../Notifications/NotificationsUtils";
import { useQueryClient } from "react-query";
import { Role } from "../../../../../../Api";
import dayjs from "../../../../../../utils/dayjs-setup";
import { getCurrentListUrl } from "../../../../../../utils/navigation";

export const AnnouncementListItem = (props: AnnouncementListItemProps) => {
	const { t } = useTranslation();
	const [deleteModalVisibility, setDeleteModalVisibility] = useState(false);
	const [spinning, setSpinning] = useState(false);
	const queryClient = useQueryClient();

	const invalidateAnnouncementsQueries = useCallback(async () => {
		await queryClient.invalidateQueries("getAnnouncements");
	}, [queryClient]);

	const deleteEntry = useCallback(() => {
		if (props.item.id) {
			setSpinning(true);
			deleteAnnouncement(props.item.id)
				.then(async () => {
					await invalidateAnnouncementsQueries();
					openNotification(
						t("announcements.delete"),
						t("announcements.deleteSuccess"),
						NOTIFICATION_TYPES.SUCCESS
					);
				})
				.catch(() => {
					openNotification(
						t("announcements.delete"),
						t("announcements.deleteError"),
						NOTIFICATION_TYPES.ERROR
					);
				})
				.finally(() => {
					setSpinning(false);
					setDeleteModalVisibility(false);
				});
		}
	}, [invalidateAnnouncementsQueries, props.item.id, t]);

	const roleLabel =
		props.item.recipientRole === Role.Student
			? t("announcements.targets.students")
			: props.item.recipientRole === Role.Professor
			? t("announcements.targets.professors")
			: props.item.recipientRole === Role.Company
			? t("announcements.targets.companies")
			: props.item.recipientRole ?? "";

	const targetsSummary = [
		roleLabel,
		props.item.faculties?.length
			? props.item.faculties.map((f) => f.facultyNameRo ?? f.id).join(", ")
			: null,
	]
		.filter(Boolean)
		.join(" – ");

	const formatDate = (d: Date | null | undefined) =>
		d ? dayjs(d).format("DD.MM.YYYY") : "";

	return (
		<div className="bg-white rounded-md shadow-md p-4 flex flex-col gap-4">
			<div className="flex flex-col gap-2">
				<Link
					to={{
						pathname: "/editare-anunt/" + props.item.id,
						state: {
							returnUrl: getCurrentListUrl(),
							searchTerm: props.searchTerm,
							currentPage: props.currentPage,
							pageSize: props.pageSize,
							origin: props.location?.pathname,
						},
					}}
					className="text-lg text-secondary font-bold line-clamp-2"
				>
					{props.item.title ?? ""}
				</Link>
				<div className="line-clamp-3 text-gray-600 text-sm">
					{props.item.description ?? ""}
				</div>
				<div className="text-xs text-gray-500">
					{t("announcements.validity")}: {formatDate(props.item.validFrom)} –{" "}
					{formatDate(props.item.validTo)}
				</div>
				{targetsSummary && (
					<div className="text-xs text-gray-600">{targetsSummary}</div>
				)}
			</div>

			<div className="mt-auto flex gap-2">
				<Button title={t("announcements.edit")}>
					<Link
						to={{
							pathname: "/editare-anunt/" + props.item.id,
							state: {
								returnUrl: getCurrentListUrl(),
								currentPage: props.currentPage,
								pageSize: props.pageSize,
								searchTerm: props.searchTerm,
							},
						}}
					>
						<FontAwesomeIcon icon={solid("edit")} /> &nbsp;
						{t("announcements.edit")}
					</Link>
				</Button>
				<Button
					danger
					onClick={() => setDeleteModalVisibility(true)}
					title={t("announcements.delete")}
					icon={<FontAwesomeIcon icon={solid("trash")} />}
				>
					{t("announcements.delete")}
				</Button>
				<ConfirmationModal
					modalText={t("announcements.deleteConfirm")}
					handleFunction={deleteEntry}
					modalVisibility={deleteModalVisibility}
					title=""
					changeModalVisibility={() => setDeleteModalVisibility(false)}
					spinning={spinning}
				/>
			</div>
		</div>
	);
};
